import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { History } from 'history';
import { defaultAdvanceFilter } from 'web/src/features/site/components/common/refine-search/common/constants';
import { APIMessages, CatalogProduct, UnitOfMeasureOrderQuantity } from '../../api/models/api-shared.models';
import { CustomerProductPriceResult } from '../../api/models/customer-product-price.models';
import {
  AdvanceFilter,
  ProductSearchFilter,
  SearchCacheEntry,
  TypeAheadRequest,
  TypeAheadResponse,
} from '../../api/models/product-catalog.models';
import { SearchProductListCatalogRequest } from '../../api/models/product-list-search.models';
import ProductListSearchService from '../../api/services/product-list-search.service';
import TypeaheadService from '../../api/services/typeahead.service';
import { useAppInsightsLogger } from '../../logging/AppInsightsLogger';
import { RecipeCatalogSearchHistoryState } from '../../models';
import { getCustomerProductPrices, updateSelectedCustomerByCustomerId } from '../common/customer.thunks';
import { setErrorDialogContent } from '../common/global.thunks';
import { isAdvanceFilterUnset, updateAdvanceFilterObject } from '../product-search-shared';
import { resetProductComparisons } from '../search/product-compare.thunks';
import { AppDispatch, AppThunk, RootState } from '../store';
import { recipeSearchSlice } from './recipe-search.slice';

const productListSearchService = ProductListSearchService.getInstance();
const typeAheadService = TypeaheadService.getInstance();
const appInsightsLogger = useAppInsightsLogger();

/*
 * Search Catalog
 */
export const resetRecipeSearchState = (): AppThunk => async (dispatch: AppDispatch) => {
  productListSearchService.abortSearchProductListCatalog();
  dispatch(recipeSearchSlice.actions.resetProductSearchState());
};

export const searchRecipeCatalog =
  (request: {
    queryText: string;
    customerId: string;
    history: History<RecipeCatalogSearchHistoryState>;
    pageIndex?: number;
  }): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(updateSelectedCustomerByCustomerId(request.customerId));

      // Handle history state
      const history = request.history;
      const historyState = history.location.state;

      const selectedCustomer = getState().customer.selectedCustomer;

      const advanceFilter = historyState?.advanceFilter ?? defaultAdvanceFilter;

      const pageSize = 25;
      const pageIndex = request.pageIndex ?? 0;
      const skip = (request.pageIndex === undefined ? 0 : getState().recipeSearch.productSearchResult?.Skip) ?? 0;

      const searchValue = request.queryText;

      if (!selectedCustomer || !advanceFilter) return;

      const apiRequest: SearchProductListCatalogRequest = {
        BusinessUnitKey: selectedCustomer.BusinessUnitKey,
        OperationCompanyNumber: selectedCustomer.OperationCompanyNumber,
        CustomerId: selectedCustomer.CustomerId,
        Skip: skip,
        PageSize: pageSize,
        QueryText: searchValue,
        AdvanceFilter: advanceFilter,
        ProductNumberSearchOnly: false,
        pageIndex,
      };

      if (!getState().recipeSearch.apiRequest) dispatch(resetProductComparisons());

      // Data in store is for search with different parameters (excluding page number and skip)
      if (
        JSON.stringify({ ...apiRequest, pageIndex: 0, Skip: 0 }) !==
        JSON.stringify({ ...getState().recipeSearch.apiRequest, pageIndex: 0, Skip: 0 })
      ) {
        dispatch(recipeSearchSlice.actions.resetProductSearchResults());
      }

      if (
        JSON.stringify({ ...apiRequest, pageIndex, Skip: skip }) ===
        JSON.stringify({ ...getState().recipeSearch.apiRequest, pageIndex: 0, Skip: 0 })
      ) {
        return;
      }

      dispatch(
        recipeSearchSlice.actions.setPageLoading({
          pageIndex: pageIndex,
          pageSize: pageSize,
          isLoading: true,
        })
      );

      const { data } = await productListSearchService.searchProductListCatalog(apiRequest);

      if (data.IsSuccess) {
        data.ResultObject.pageIndex = apiRequest.pageIndex;

        if (isAdvanceFilterUnset(history.location.state?.advanceFilter)) {
          history.replace(history.location.pathname + history.location.search, {
            ...history.location.state,
            cachedFacets: data.ResultObject.Facets,
          });
        }

        dispatch(
          recipeSearchSlice.actions.setProductSearchResults({
            request: apiRequest,
            result: data.ResultObject,
          })
        );
      } else {
        dispatch(setErrorDialogContent('Error occurred', data.ErrorMessages));
        dispatch(
          recipeSearchSlice.actions.setPageLoaded({
            pageIndex: pageIndex,
            catalogProducts: [],
          })
        );
      }
    } catch (error) {
      console.error(error);
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const setRecipeSearchPageLoading =
  (pageIndex: number, callBack?: () => void | Promise<void>): AppThunk =>
  async (dispatch: AppDispatch) => {
    const pageSize = 25;
    dispatch(
      recipeSearchSlice.actions.setPageLoading({
        pageIndex: pageIndex,
        pageSize: pageSize,
        isLoading: true,
      })
    );
    callBack?.();
  };

/*
 * Search Filters
 */
export const updateRecipeSearchFilters =
  (history: History<{ advanceFilter?: AdvanceFilter }>, ...filters: ProductSearchFilter[]): AppThunk =>
  (dispatch: AppDispatch) => {
    dispatch(recipeSearchSlice.actions.resetProductSearchResults());
    const advanceFilter = updateAdvanceFilterObject(history.location.state?.advanceFilter, filters);
    history.replace(history.location.pathname + history.location.search, {
      ...history.location.state,
      advanceFilter,
    });
  };

export const clearRecipeSearchFilters =
  (history: History<RecipeCatalogSearchHistoryState>): AppThunk =>
  (dispatch: AppDispatch) => {
    dispatch(recipeSearchSlice.actions.resetProductSearchResults());
    history.replace(history.location.pathname + history.location.search, {
      ...history.location.state,
      advanceFilter: undefined,
    });
  };

/*
 * Typeahead
 */
export const getRecipeTypeAheadSearchResults =
  (searchValue: string, isCalledFromDeleteFlow = false): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const request: TypeAheadRequest = { SearchValue: searchValue };

    const cache = getState().recipeSearch.searchCache;
    let isCacheHit = false;
    if (!isCalledFromDeleteFlow && cache) {
      const cacheHitResult = cache.find((c) => c.key.toLowerCase() === request.SearchValue.toLowerCase());
      if (cacheHitResult) {
        isCacheHit = true;
        dispatch(recipeSearchSlice.actions.setTypeAheadResults(cacheHitResult.value));
      }
    }
    if (!isCacheHit || isCalledFromDeleteFlow) {
      try {
        const selectedCustomer = getState().customer.selectedCustomer;
        request.BusinessUnitKey = selectedCustomer?.BusinessUnitKey;
        request.CustomerId = selectedCustomer?.CustomerId;
        const { data: result } = await typeAheadService.getTypeAheadSearchResults(request);
        const typeAheadAPIMessages: APIMessages = {
          IsError: false,
        };
        if (result.IsSuccess) {
          const typeAheadResult: TypeAheadResponse = result.ResultObject;
          if (result.InformationMessages && result.InformationMessages.length > 0) {
            typeAheadAPIMessages.InformationMessages = result.InformationMessages;
            dispatch(recipeSearchSlice.actions.setTypeAheadAPIMessages(typeAheadAPIMessages));
          }
          if (request.SearchValue.toLowerCase() !== '') {
            dispatch(
              recipeSearchSlice.actions.setSearchCache(
                updateCache(cache, isCalledFromDeleteFlow, request.SearchValue, result.ResultObject)
              )
            );
          }

          dispatch(recipeSearchSlice.actions.setTypeAheadResults(typeAheadResult));
        } else {
          if (result.ErrorMessages && result.ErrorMessages.length > 0) {
            typeAheadAPIMessages.ErrorMessages = result.ErrorMessages;
            typeAheadAPIMessages.IsError = true;
            dispatch(recipeSearchSlice.actions.setTypeAheadAPIMessages(typeAheadAPIMessages));
          }
        }
      } catch (error) {
        appInsightsLogger.trackException({
          exception: error,
          severityLevel: SeverityLevel.Error,
        });
      }
    }
  };

const updateCache = (
  cache: SearchCacheEntry[] | undefined,
  isCalledFromDeleteFlow: boolean,
  searchValue: string,
  resultObject: TypeAheadResponse
): SearchCacheEntry[] => {
  // update cache with new data
  let cacheCopy: SearchCacheEntry[] = [];
  if (cache) {
    if (isCalledFromDeleteFlow) {
      cacheCopy = cache.filter((f) => f.key.toLowerCase() !== searchValue);
    } else {
      cacheCopy = cache.map((c) => {
        return c;
      });
    }
  }
  const searcCacheEntry: SearchCacheEntry = {
    key: searchValue,
    value: resultObject,
  };
  cacheCopy.push(searcCacheEntry);
  return cacheCopy;
};

export const deleteRecipeSearchHistory =
  (searchValue: string, userCustomerSearchHistoryId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const { data: result } = await typeAheadService.deleteSearchHistory({
        UserCustomerSearchHistoryId: userCustomerSearchHistoryId,
      });
      if (result.IsSuccess) {
        dispatch(getRecipeTypeAheadSearchResults(searchValue, true));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const clearRecipeSearchTypeAheadResults = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(
    recipeSearchSlice.actions.setTypeAheadResults({
      Suggestions: [],
      SearchHistory: [],
    })
  );
};

/**
 * Calls the SearchProductListCatalog API call
 *
 * @param queryText - The query to the product list catalog
 * @param onSuccess - Method to call on success
 * @param onPricingSuccess - Method to call when GetCustomerProductPrice API call is a success
 * @param onError - Method to call on error
 * @param doNotGetPrice - Stops the method from calling the GetCustomerProductPrice API call
 * @returns NULL
 */
export const getProductListCatalog =
  (
    queryText: string,
    onSuccess?: (product: CatalogProduct) => void,
    onPricingSuccess?: (product: CatalogProduct) => void,
    onError?: (errors: string[]) => void,
    doNotGetPrice?: boolean,
    includeInactiveProducts?: boolean
  ): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const customer = getState().customer.selectedCustomer;
      if (!customer) return;
      if (!queryText) return;

      const request: SearchProductListCatalogRequest = {
        BusinessUnitKey: customer.BusinessUnitKey,
        OperationCompanyNumber: customer.OperationCompanyNumber,
        CustomerId: customer.CustomerId,
        Skip: 0,
        PageSize: 1,
        QueryText: queryText,
        ProductNumberSearchOnly: true,
        pageIndex: 0,
        IncludeInactiveProducts: includeInactiveProducts,
      };

      const { data } = await productListSearchService.searchProductListCatalog(request);

      if (data.IsSuccess && data.ResultObject.CatalogProducts?.length > 0) {
        const product = data.ResultObject.CatalogProducts[0].Product;
        onSuccess?.(product);
        if (!doNotGetPrice) {
          dispatch(
            getCustomerProductPrices(
              product.UnitOfMeasureOrderQuantities.map((uom) => {
                return { ProductKey: product.ProductKey, UnitOfMeasureType: uom.UnitOfMeasure };
              }),
              (priceResults: CustomerProductPriceResult[]) => {
                let productUom: UnitOfMeasureOrderQuantity | undefined = undefined;

                onPricingSuccess?.({
                  ...product,
                  UnitOfMeasureOrderQuantities: priceResults.map((p, idx) => {
                    productUom = product.UnitOfMeasureOrderQuantities.find(
                      (uom) => uom.UnitOfMeasure === p.UnitOfMeasureType
                    );
                    if (productUom) {
                      return {
                        ...productUom,
                        Price: p.Price,
                      };
                    }
                    return product.UnitOfMeasureOrderQuantities[idx];
                  }),
                });
              }
            )
          );
        }
      } else {
        if (data.IsSuccess) onError?.(data.ErrorMessages);
        else dispatch(setErrorDialogContent('Error occurred', data.ErrorMessages));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    } finally {
    }
  };
